/**
 * Flash-cartoon drawing helpers shared by the field renderer, the mole and the UI previews:
 * thick warm ink outlines, flat fills with one hard highlight, and the stock props of a
 * Raft Wars afternoon (clouds, sun, palms, barrels, tennis balls). Everything here draws in
 * the current transform and leaves the context's fill/stroke state dirty.
 */

export const INK = '#2a1a12';
export const INK_W = 3;

/** Rounded-rectangle path (no fill). */
export function rr(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number): void {
  const rad = Math.max(0, Math.min(r, w / 2, h / 2));
  ctx.beginPath();
  ctx.moveTo(x + rad, y);
  ctx.arcTo(x + w, y, x + w, y + h, rad);
  ctx.arcTo(x + w, y + h, x, y + h, rad);
  ctx.arcTo(x, y + h, x, y, rad);
  ctx.arcTo(x, y, x + w, y, rad);
  ctx.closePath();
}

function parseHex(hex: string): [number, number, number] | null {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return null;
  const n = parseInt(m[1]!, 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

function toHex(r: number, g: number, b: number): string {
  const ch = (v: number) => Math.max(0, Math.min(255, Math.round(v)));
  return `#${((ch(r) << 16) | (ch(g) << 8) | ch(b)).toString(16).padStart(6, '0')}`;
}

/** Lighten (positive) or darken (negative) a hex colour by a flat amount per channel. */
export function shade(hex: string, amount: number): string {
  const c = parseHex(hex);
  if (!c) return hex;
  return toHex(c[0] + amount, c[1] + amount, c[2] + amount);
}

/** Linear blend of two hex colours. */
export function mix(a: string, b: string, t: number): string {
  const ca = parseHex(a);
  const cb = parseHex(b);
  if (!ca || !cb) return a;
  return toHex(ca[0] + (cb[0] - ca[0]) * t, ca[1] + (cb[1] - ca[1]) * t, ca[2] + (cb[2] - ca[2]) * t);
}

/** Perceived brightness 0..1 of a hex colour; 0.5 for anything unparseable. */
export function luminance(hex: string): number {
  const c = parseHex(hex);
  if (!c) return 0.5;
  return (0.299 * c[0] + 0.587 * c[1] + 0.114 * c[2]) / 255;
}

/** Fill the current path and run an ink outline round it. */
export function ink(ctx: CanvasRenderingContext2D, fill: string, width = INK_W, color = INK): void {
  ctx.fillStyle = fill;
  ctx.fill();
  ctx.lineWidth = width;
  ctx.lineJoin = 'round';
  ctx.strokeStyle = color;
  ctx.stroke();
}

/** A thick line with an ink rim: a limb, a rope, a plank edge. */
export function inkLine(ctx: CanvasRenderingContext2D, x0: number, y0: number, x1: number, y1: number, width: number, color: string, rim = 1.5): void {
  ctx.lineCap = 'round';
  ctx.beginPath();
  ctx.moveTo(x0, y0);
  ctx.lineTo(x1, y1);
  ctx.strokeStyle = INK;
  ctx.lineWidth = width + rim * 2;
  ctx.stroke();
  ctx.strokeStyle = color;
  ctx.lineWidth = width;
  ctx.stroke();
}

function cloudPath(ctx: CanvasRenderingContext2D, dy: number): void {
  const puffs: [number, number, number][] = [
    [-1.05, -0.05, 0.4],
    [-0.55, -0.4, 0.55],
    [0.05, -0.52, 0.64],
    [0.65, -0.34, 0.52],
    [1.1, -0.02, 0.38],
  ];
  ctx.beginPath();
  for (const [px, py, r] of puffs) {
    ctx.moveTo(px + r, py + dy);
    ctx.arc(px, py + dy, r, 0, Math.PI * 2);
  }
  ctx.moveTo(-1.2, -0.3 + dy);
  ctx.rect(-1.2, -0.3 + dy, 2.5, 0.5);
}

/** A puffy cloud with a flat belly; `s` is roughly its half-width in px. */
export function drawCloud(ctx: CanvasRenderingContext2D, x: number, y: number, s: number, color: string, under: string): void {
  ctx.save();
  ctx.translate(x, y);
  ctx.scale(s, s);
  ctx.fillStyle = under;
  cloudPath(ctx, 0.12);
  ctx.fill();
  ctx.fillStyle = color;
  cloudPath(ctx, 0);
  ctx.fill();
  ctx.restore();
}

/** A cartoon sun: pale halo, slow rays, warm disc. */
export function drawSun(ctx: CanvasRenderingContext2D, x: number, y: number, r: number, now: number): void {
  ctx.save();
  ctx.translate(x, y);
  const halo = ctx.createRadialGradient(0, 0, r * 0.6, 0, 0, r * 3.2);
  halo.addColorStop(0, 'rgba(255, 240, 170, 0.55)');
  halo.addColorStop(1, 'rgba(255, 240, 170, 0)');
  ctx.fillStyle = halo;
  ctx.beginPath();
  ctx.arc(0, 0, r * 3.2, 0, Math.PI * 2);
  ctx.fill();
  ctx.rotate(now / 14000);
  ctx.fillStyle = 'rgba(255, 236, 140, 0.35)';
  for (let i = 0; i < 12; i++) {
    const a = (i / 12) * Math.PI * 2;
    ctx.beginPath();
    ctx.moveTo(Math.cos(a - 0.09) * r * 1.15, Math.sin(a - 0.09) * r * 1.15);
    ctx.lineTo(Math.cos(a) * r * 2.3, Math.sin(a) * r * 2.3);
    ctx.lineTo(Math.cos(a + 0.09) * r * 1.15, Math.sin(a + 0.09) * r * 1.15);
    ctx.closePath();
    ctx.fill();
  }
  ctx.beginPath();
  ctx.arc(0, 0, r, 0, Math.PI * 2);
  ctx.fillStyle = '#ffe56b';
  ctx.fill();
  ctx.lineWidth = r * 0.14;
  ctx.strokeStyle = '#f6b73c';
  ctx.stroke();
  ctx.restore();
}

/** A flat palm tree silhouette for the horizon; (x, y) is the foot of the trunk. */
export function drawPalm(ctx: CanvasRenderingContext2D, x: number, y: number, h: number, trunk: string, leaf: string, lean = 1): void {
  ctx.save();
  ctx.translate(x, y);
  ctx.strokeStyle = trunk;
  ctx.lineWidth = Math.max(2, h * 0.07);
  ctx.lineCap = 'round';
  ctx.beginPath();
  ctx.moveTo(0, 0);
  ctx.quadraticCurveTo(h * 0.08 * lean, -h * 0.55, h * 0.22 * lean, -h);
  ctx.stroke();
  const tx = h * 0.22 * lean;
  const ty = -h;
  ctx.fillStyle = leaf;
  for (let i = 0; i < 6; i++) {
    const a = -Math.PI * 0.95 + (i / 5) * Math.PI * 0.9 + (lean < 0 ? Math.PI * 0.1 : 0);
    ctx.save();
    ctx.translate(tx, ty);
    ctx.rotate(a);
    ctx.beginPath();
    ctx.ellipse(h * 0.2, 0, h * 0.24, h * 0.07, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }
  ctx.beginPath();
  ctx.arc(tx, ty + h * 0.02, h * 0.06, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
}

/** A barrel standing on (x, y): the raft stops. */
export function drawBarrel(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, color: string): void {
  ctx.save();
  ctx.translate(x, y);
  rr(ctx, -w / 2, -h, w, h, w * 0.22);
  ink(ctx, color);
  ctx.fillStyle = 'rgba(255,255,255,0.22)';
  rr(ctx, -w / 2 + 3, -h + 3, w * 0.22, h - 6, 3);
  ctx.fill();
  ctx.fillStyle = shade(color, -55);
  for (const fy of [0.28, 0.72]) {
    ctx.fillRect(-w / 2, -h + h * fy - 2, w, 4);
  }
  ctx.strokeStyle = INK;
  ctx.lineWidth = 1.5;
  for (const fy of [0.28, 0.72]) {
    ctx.beginPath();
    ctx.moveTo(-w / 2, -h + h * fy + 2);
    ctx.lineTo(w / 2, -h + h * fy + 2);
    ctx.stroke();
  }
  ctx.restore();
}

export function drawTennisBall(ctx: CanvasRenderingContext2D, x: number, y: number, r: number, rot: number): void {
  ctx.save();
  ctx.translate(x, y);
  ctx.rotate(rot);
  ctx.beginPath();
  ctx.arc(0, 0, r, 0, Math.PI * 2);
  ink(ctx, '#dcef52', Math.min(INK_W, r * 0.4));
  ctx.strokeStyle = '#fbfff0';
  ctx.lineWidth = Math.max(1.2, r * 0.22);
  ctx.beginPath();
  ctx.arc(r * 0.9, 0, r * 0.95, Math.PI * 0.72, Math.PI * 1.28);
  ctx.stroke();
  ctx.beginPath();
  ctx.arc(-r * 0.9, 0, r * 0.95, -Math.PI * 0.28, Math.PI * 0.28);
  ctx.stroke();
  ctx.restore();
}

export function drawGrenade(ctx: CanvasRenderingContext2D, x: number, y: number, r: number, rot: number): void {
  ctx.save();
  ctx.translate(x, y);
  ctx.rotate(rot);
  // Neck, lever and pin ring behind the body
  ctx.beginPath();
  ctx.rect(-r * 0.38, -r * 1.45, r * 0.76, r * 0.6);
  ink(ctx, '#aab3bb', 2);
  ctx.beginPath();
  ctx.moveTo(r * 0.3, -r * 1.35);
  ctx.quadraticCurveTo(r * 1.2, -r * 1.3, r * 1.1, -r * 0.3);
  ctx.strokeStyle = INK;
  ctx.lineWidth = 4;
  ctx.stroke();
  ctx.strokeStyle = '#c7ced4';
  ctx.lineWidth = 1.8;
  ctx.stroke();
  ctx.beginPath();
  ctx.arc(-r * 0.75, -r * 1.3, r * 0.3, 0, Math.PI * 2);
  ctx.strokeStyle = INK;
  ctx.lineWidth = 2.2;
  ctx.stroke();
  // Body
  ctx.beginPath();
  ctx.ellipse(0, 0, r, r * 1.12, 0, 0, Math.PI * 2);
  ink(ctx, '#5c8a3f', Math.min(INK_W, r * 0.35));
  ctx.save();
  ctx.clip();
  ctx.strokeStyle = 'rgba(20, 35, 10, 0.4)';
  ctx.lineWidth = 1.4;
  for (const o of [-0.45, 0, 0.45]) {
    ctx.beginPath();
    ctx.moveTo(o * r, -r * 1.2);
    ctx.lineTo(o * r, r * 1.2);
    ctx.moveTo(-r * 1.2, o * r);
    ctx.lineTo(r * 1.2, o * r);
    ctx.stroke();
  }
  ctx.fillStyle = 'rgba(255,255,255,0.22)';
  ctx.beginPath();
  ctx.ellipse(-r * 0.4, -r * 0.45, r * 0.25, r * 0.35, 0.5, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
  ctx.restore();
}

/** A rocket pointing along +x after the caller's rotate; `flame` 0..1 flickers the exhaust. */
export function drawRocket(ctx: CanvasRenderingContext2D, homing: boolean, flame: number): void {
  const body = homing ? '#ff5fa0' : '#f3efe4';
  const trim = homing ? '#8a1f54' : '#e8453c';
  if (flame > 0) {
    const f = 10 + flame * 9;
    ctx.fillStyle = 'rgba(255, 140, 40, 0.9)';
    ctx.beginPath();
    ctx.moveTo(-12, -5);
    ctx.lineTo(-12 - f, 0);
    ctx.lineTo(-12, 5);
    ctx.closePath();
    ctx.fill();
    ctx.fillStyle = '#ffe36b';
    ctx.beginPath();
    ctx.moveTo(-12, -2.5);
    ctx.lineTo(-12 - f * 0.55, 0);
    ctx.lineTo(-12, 2.5);
    ctx.closePath();
    ctx.fill();
  }
  // Fins
  ctx.beginPath();
  ctx.moveTo(-12, -4);
  ctx.lineTo(-16, -10);
  ctx.lineTo(-6, -4);
  ctx.closePath();
  ink(ctx, trim, 2.5);
  ctx.beginPath();
  ctx.moveTo(-12, 4);
  ctx.lineTo(-16, 10);
  ctx.lineTo(-6, 4);
  ctx.closePath();
  ink(ctx, trim, 2.5);
  // Body and nose
  rr(ctx, -13, -5.5, 23, 11, 4);
  ink(ctx, body, 2.5);
  ctx.beginPath();
  ctx.moveTo(9, -5.5);
  ctx.quadraticCurveTo(17, -3, 19, 0);
  ctx.quadraticCurveTo(17, 3, 9, 5.5);
  ctx.closePath();
  ink(ctx, trim, 2.5);
  ctx.beginPath();
  ctx.arc(1, 0, 2.6, 0, Math.PI * 2);
  ink(ctx, '#8fdcff', 1.5);
}

/** The flash of an explosion: a jagged star that swells then fades. t is 0..1 over its life. */
export function drawBurst(ctx: CanvasRenderingContext2D, x: number, y: number, r: number, t: number): void {
  const grow = Math.sin(Math.min(1, t * 1.6) * Math.PI * 0.5);
  const R = r * (0.45 + 0.75 * grow);
  ctx.save();
  ctx.translate(x, y);
  ctx.rotate(t * 0.6);
  ctx.globalAlpha = Math.max(0, 1 - t * t * 1.1);
  ctx.beginPath();
  const points = 11;
  for (let i = 0; i < points * 2; i++) {
    const a = (i / (points * 2)) * Math.PI * 2;
    const rad = i % 2 === 0 ? R : R * (0.56 + 0.08 * Math.sin(i * 2.1));
    const px = Math.cos(a) * rad;
    const py = Math.sin(a) * rad;
    if (i === 0) ctx.moveTo(px, py);
    else ctx.lineTo(px, py);
  }
  ctx.closePath();
  ctx.fillStyle = '#ffe14d';
  ctx.fill();
  ctx.lineWidth = Math.max(3, R * 0.09);
  ctx.lineJoin = 'round';
  ctx.strokeStyle = '#ff6a1a';
  ctx.stroke();
  ctx.fillStyle = 'rgba(255,255,255,0.9)';
  ctx.beginPath();
  ctx.arc(0, 0, R * 0.38 * (1 - t * 0.5), 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
}

/** A smoke / steam puff: a flat disc with a lit top-left. */
export function drawPuff(ctx: CanvasRenderingContext2D, x: number, y: number, r: number, color: string, light: string, alpha: number): void {
  ctx.globalAlpha = alpha;
  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.arc(x, y, r, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = light;
  ctx.beginPath();
  ctx.arc(x - r * 0.3, y - r * 0.3, r * 0.5, 0, Math.PI * 2);
  ctx.fill();
  ctx.globalAlpha = 1;
}

/** A water splash: a column that leaps then falls back, and a widening foam ring at the surface. */
export function drawSplash(ctx: CanvasRenderingContext2D, x: number, y: number, t: number): void {
  const H = 110 * Math.sin(Math.min(1, t) * Math.PI);
  const w = 22 + 26 * t;
  ctx.save();
  ctx.globalAlpha = 1 - t * t;
  rr(ctx, x - w / 2, y - H, w, H + 6, w / 2);
  ctx.fillStyle = 'rgba(236, 248, 255, 0.92)';
  ctx.fill();
  ctx.lineWidth = 2.5;
  ctx.strokeStyle = 'rgba(110, 175, 230, 0.9)';
  ctx.stroke();
  ctx.fillStyle = 'rgba(160, 210, 245, 0.7)';
  rr(ctx, x - w / 2 + 4, y - H + 8, w * 0.32, Math.max(0, H - 14), w * 0.16);
  ctx.fill();
  ctx.strokeStyle = 'rgba(255,255,255,0.9)';
  ctx.lineWidth = 3;
  ctx.beginPath();
  ctx.ellipse(x, y, 14 + t * 80, 5 + t * 14, 0, 0, Math.PI * 2);
  ctx.stroke();
  ctx.restore();
}

/** A cartoon tombstone with a team badge. */
export function drawGrave(ctx: CanvasRenderingContext2D, x: number, y: number, team: string): void {
  ctx.beginPath();
  ctx.moveTo(x - 11, y);
  ctx.lineTo(x - 11, y - 20);
  ctx.arc(x, y - 20, 11, Math.PI, 0);
  ctx.lineTo(x + 11, y);
  ctx.closePath();
  ink(ctx, '#9ea4ad');
  ctx.fillStyle = '#c9ced6';
  ctx.beginPath();
  ctx.moveTo(x - 8, y - 4);
  ctx.lineTo(x - 8, y - 20);
  ctx.arc(x, y - 20, 8, Math.PI, Math.PI * 1.5);
  ctx.lineTo(x - 2, y - 4);
  ctx.closePath();
  ctx.fill();
  ctx.fillStyle = team;
  rr(ctx, x - 4, y - 22, 8, 8, 2);
  ink(ctx, team, 1.5);
}

/** A big grenade: the pineapple with a steel band round its middle. */
export function drawBigGrenade(ctx: CanvasRenderingContext2D, x: number, y: number, r: number, rot: number): void {
  drawGrenade(ctx, x, y, r, rot);
  ctx.save();
  ctx.translate(x, y);
  ctx.rotate(rot);
  ctx.beginPath();
  ctx.rect(-r * 1.02, -r * 0.2, r * 2.04, r * 0.4);
  ink(ctx, '#aab3bb', 2);
  ctx.restore();
}

/** A cluster bomb: a grey ball with a yellow band and rivets; `frag` draws one of its bomblets. */
export function drawCluster(ctx: CanvasRenderingContext2D, x: number, y: number, r: number, rot: number, frag: boolean): void {
  ctx.save();
  ctx.translate(x, y);
  ctx.rotate(rot);
  ctx.beginPath();
  ctx.arc(0, 0, r, 0, Math.PI * 2);
  ink(ctx, frag ? '#f0b429' : '#6d7681', Math.min(INK_W, r * 0.35));
  if (frag) {
    ctx.fillStyle = 'rgba(255,255,255,0.35)';
    ctx.beginPath();
    ctx.arc(-r * 0.3, -r * 0.3, r * 0.3, 0, Math.PI * 2);
    ctx.fill();
  } else {
    ctx.save();
    ctx.beginPath();
    ctx.arc(0, 0, r - 1, 0, Math.PI * 2);
    ctx.clip();
    ctx.fillStyle = '#f0b429';
    ctx.fillRect(-r, -r * 0.22, r * 2, r * 0.44);
    ctx.fillStyle = INK;
    for (const a of [-0.75, 0, 0.75]) {
      ctx.beginPath();
      ctx.arc(Math.sin(a) * r * 0.62, -Math.cos(a) * r * 0.62, r * 0.12, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.restore();
  }
  ctx.restore();
}

/** A mine: a flat disc with a dome, three prongs and a light. `hot` 0..1 lights the lamp. */
export function drawMine(ctx: CanvasRenderingContext2D, x: number, y: number, r: number, armed: boolean, hot: number): void {
  ctx.save();
  ctx.translate(x, y);
  ctx.beginPath();
  ctx.ellipse(0, 0, r, r * 0.42, 0, 0, Math.PI * 2);
  ink(ctx, '#4b525b', Math.min(INK_W, r * 0.3));
  ctx.beginPath();
  ctx.ellipse(0, -r * 0.3, r * 0.68, r * 0.5, 0, Math.PI, Math.PI * 2);
  ctx.lineTo(r * 0.68, -r * 0.3);
  ctx.closePath();
  ink(ctx, '#5d6670', Math.min(INK_W, r * 0.3));
  for (const a of [-0.6, 0, 0.6]) {
    inkLine(ctx, Math.sin(a) * r * 0.4, -r * 0.75, Math.sin(a) * r * 0.6, -r * 1.15, 3, '#aab3bb', 1.2);
  }
  ctx.beginPath();
  ctx.arc(0, -r * 0.55, r * 0.2, 0, Math.PI * 2);
  ink(ctx, armed ? (hot > 0.5 ? '#ff3b3b' : '#7a1a1a') : '#38c172', 1.5);
  if (armed && hot > 0.5) {
    ctx.fillStyle = 'rgba(255,59,59,0.35)';
    ctx.beginPath();
    ctx.arc(0, -r * 0.55, r * 0.5, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.restore();
}

/** A grapple hook pointing along +x after the caller's rotate: a shank and three tines. */
export function drawHook(ctx: CanvasRenderingContext2D, s = 1): void {
  ctx.save();
  ctx.scale(s, s);
  inkLine(ctx, -12, 0, 2, 0, 4.5, '#8a949e', 1.5);
  for (const a of [-1, 0, 1]) {
    ctx.beginPath();
    ctx.moveTo(0, 0);
    ctx.quadraticCurveTo(13, a * 9, 1, a * 13 + (a === 0 ? 0 : 0));
    if (a === 0) {
      ctx.moveTo(0, 0);
      ctx.lineTo(14, 0);
    }
    ctx.strokeStyle = INK;
    ctx.lineWidth = 6;
    ctx.lineCap = 'round';
    ctx.stroke();
    ctx.strokeStyle = '#aab3bb';
    ctx.lineWidth = 3;
    ctx.stroke();
  }
  ctx.beginPath();
  ctx.arc(-12, 0, 3.5, 0, Math.PI * 2);
  ink(ctx, '#6d7681', 2);
  ctx.restore();
}

/** A plank (a thrown platform) with board seams. */
export function drawPlank(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, rot: number): void {
  ctx.save();
  ctx.translate(x, y);
  ctx.rotate(rot);
  rr(ctx, -w / 2, -h / 2, w, h, 3);
  ink(ctx, '#c98c4e');
  ctx.fillStyle = 'rgba(42,26,18,0.35)';
  for (let sx = -w / 2 + 24; sx < w / 2 - 4; sx += 26) ctx.fillRect(sx, -h / 2 + 1, 2, h - 2);
  ctx.fillStyle = 'rgba(255,255,255,0.25)';
  ctx.fillRect(-w / 2 + 3, -h / 2 + 2, w - 6, 2.5);
  ctx.restore();
}

/** A bat, a mallet or a pike held at the origin and pointing along +x. */
export function drawMeleeWeapon(ctx: CanvasRenderingContext2D, kind: 'bat' | 'mallet' | 'pike' | 'pickaxe'): void {
  ctx.lineCap = 'round';
  if (kind === 'pickaxe') {
    inkLine(ctx, -6, 0, 26, 0, 4, '#b0813f', 1.5);
    ctx.beginPath();
    ctx.moveTo(14, -16);
    ctx.quadraticCurveTo(28, -2, 14, 16);
    ctx.quadraticCurveTo(22, 0, 14, -16);
    ctx.closePath();
    ink(ctx, '#7d8793', 2.5);
  } else if (kind === 'bat') {
    ctx.beginPath();
    ctx.moveTo(-6, 0);
    ctx.lineTo(30, 0);
    ctx.strokeStyle = INK;
    ctx.lineWidth = 11;
    ctx.stroke();
    ctx.strokeStyle = '#d2a064';
    ctx.lineWidth = 7.5;
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(-6, 0);
    ctx.lineTo(8, 0);
    ctx.strokeStyle = '#8a5a2a';
    ctx.lineWidth = 5;
    ctx.stroke();
  } else if (kind === 'mallet') {
    inkLine(ctx, -6, 0, 24, 0, 4.5, '#b0813f', 1.5);
    rr(ctx, 18, -9, 14, 18, 3);
    ink(ctx, '#7d8793');
    ctx.fillStyle = 'rgba(255,255,255,0.3)';
    ctx.fillRect(20, -7, 3, 14);
  } else {
    inkLine(ctx, -10, 0, 40, 0, 3.5, '#b0813f', 1.3);
    ctx.beginPath();
    ctx.moveTo(52, 0);
    ctx.lineTo(38, -5);
    ctx.lineTo(38, 5);
    ctx.closePath();
    ink(ctx, '#d8dde2', 2);
  }
}

/** A mortar shell pointing along +x after the caller's rotate: a dark finned teardrop with a pale nose band. */
export function drawShell(ctx: CanvasRenderingContext2D): void {
  ctx.beginPath();
  ctx.moveTo(14, 0);
  ctx.quadraticCurveTo(6, -8, -8, -6);
  ctx.lineTo(-14, -9);
  ctx.lineTo(-13, -3);
  ctx.lineTo(-13, 3);
  ctx.lineTo(-14, 9);
  ctx.lineTo(-8, 6);
  ctx.quadraticCurveTo(6, 8, 14, 0);
  ctx.closePath();
  ink(ctx, '#4f5964', 2.5);
  ctx.fillStyle = '#e8c15a';
  ctx.beginPath();
  ctx.moveTo(14, 0);
  ctx.quadraticCurveTo(10, -4, 7, -5);
  ctx.lineTo(7, 5);
  ctx.quadraticCurveTo(10, 4, 14, 0);
  ctx.closePath();
  ctx.fill();
  ctx.fillStyle = 'rgba(255,255,255,0.25)';
  ctx.fillRect(-7, -4, 12, 2);
}

/** A four-point spark: the lit end of a fuse. `s` is its size; it is meant to flicker from frame to frame. */
export function drawSpark(ctx: CanvasRenderingContext2D, x: number, y: number, s: number): void {
  ctx.fillStyle = 'rgba(255, 150, 40, 0.55)';
  ctx.beginPath();
  ctx.arc(x, y, s * 1.6, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = '#fff3a0';
  ctx.beginPath();
  ctx.moveTo(x, y - s);
  ctx.lineTo(x + s * 0.35, y - s * 0.35);
  ctx.lineTo(x + s, y);
  ctx.lineTo(x + s * 0.35, y + s * 0.35);
  ctx.lineTo(x, y + s);
  ctx.lineTo(x - s * 0.35, y + s * 0.35);
  ctx.lineTo(x - s, y);
  ctx.lineTo(x - s * 0.35, y - s * 0.35);
  ctx.closePath();
  ctx.fill();
}

/** A sticky bomb: a green goo blob with two drips and a dark rim. `pulse` 0..1 breathes the highlight so it reads as wet. */
export function drawSticky(ctx: CanvasRenderingContext2D, x: number, y: number, r: number, pulse: number): void {
  const rim = '#1f4a10';
  ctx.save();
  ctx.translate(x, y);
  // Drips first, so the body's outline covers where they join it.
  ctx.beginPath();
  ctx.arc(-r * 0.6, r * 0.6, r * 0.42, 0, Math.PI * 2);
  ink(ctx, '#6cc932', 2, rim);
  ctx.beginPath();
  ctx.arc(r * 0.62, r * 0.5, r * 0.32, 0, Math.PI * 2);
  ink(ctx, '#6cc932', 2, rim);
  ctx.beginPath();
  ctx.arc(0, 0, r, 0, Math.PI * 2);
  ink(ctx, '#7fd93a', Math.min(INK_W, r * 0.35), rim);
  ctx.fillStyle = `rgba(255,255,255,${0.3 + 0.4 * pulse})`;
  ctx.beginPath();
  ctx.ellipse(-r * 0.36, -r * 0.4, r * (0.26 + 0.1 * pulse), r * (0.18 + 0.08 * pulse), -0.6, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
}

/** A rubber bomb: a red bouncy ball with a seam that spins and a fixed white highlight. */
export function drawRubberBall(ctx: CanvasRenderingContext2D, x: number, y: number, r: number, rot: number): void {
  ctx.save();
  ctx.translate(x, y);
  ctx.save();
  ctx.rotate(rot);
  ctx.beginPath();
  ctx.arc(0, 0, r, 0, Math.PI * 2);
  ink(ctx, '#e8453c', Math.min(INK_W, r * 0.35));
  ctx.strokeStyle = 'rgba(110, 20, 15, 0.55)';
  ctx.lineWidth = Math.max(1.2, r * 0.18);
  ctx.beginPath();
  ctx.arc(r * 0.85, 0, r * 0.9, Math.PI * 0.72, Math.PI * 1.28);
  ctx.stroke();
  ctx.beginPath();
  ctx.arc(-r * 0.85, 0, r * 0.9, -Math.PI * 0.28, Math.PI * 0.28);
  ctx.stroke();
  ctx.restore();
  ctx.fillStyle = 'rgba(255,255,255,0.85)';
  ctx.beginPath();
  ctx.ellipse(-r * 0.36, -r * 0.4, r * 0.26, r * 0.17, -0.7, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
}

/** An airstrike bomb pointing along +x after the caller's rotate: a grey finned teardrop with a yellow nose band. */
export function drawAirBomb(ctx: CanvasRenderingContext2D): void {
  for (const s of [-1, 1]) {
    ctx.beginPath();
    ctx.moveTo(-7, s * 5);
    ctx.lineTo(-17, s * 11);
    ctx.lineTo(-17, s * 4);
    ctx.closePath();
    ink(ctx, '#5d6670', 2);
  }
  ctx.beginPath();
  ctx.moveTo(14, 0);
  ctx.quadraticCurveTo(10, -7, -4, -7);
  ctx.lineTo(-15, -5);
  ctx.lineTo(-15, 5);
  ctx.lineTo(-4, 7);
  ctx.quadraticCurveTo(10, 7, 14, 0);
  ctx.closePath();
  ink(ctx, '#7d8793', 2.5);
  ctx.fillStyle = '#f0b429';
  ctx.beginPath();
  ctx.moveTo(14, 0);
  ctx.quadraticCurveTo(11, -4.5, 7, -5.8);
  ctx.lineTo(7, 5.8);
  ctx.quadraticCurveTo(11, 4.5, 14, 0);
  ctx.closePath();
  ctx.fill();
  ctx.fillStyle = 'rgba(255,255,255,0.3)';
  ctx.fillRect(-9, -4.5, 13, 2);
}

/** The massive bomb: a big black sphere with a steel cap, a yellow trefoil and a lit fuse. `spark` 0..1 flickers the fuse tip. */
export function drawMassive(ctx: CanvasRenderingContext2D, x: number, y: number, r: number, rot: number, spark: number): void {
  ctx.save();
  ctx.translate(x, y);
  ctx.rotate(rot);
  // Cap and fuse behind the body
  rr(ctx, -r * 0.3, -r * 1.3, r * 0.6, r * 0.45, 2);
  ink(ctx, '#aab3bb', 2);
  ctx.beginPath();
  ctx.moveTo(0, -r * 1.25);
  ctx.quadraticCurveTo(r * 0.45, -r * 1.85, r * 0.9, -r * 1.5);
  ctx.strokeStyle = INK;
  ctx.lineWidth = 4;
  ctx.lineCap = 'round';
  ctx.stroke();
  ctx.strokeStyle = '#d8c48a';
  ctx.lineWidth = 2;
  ctx.stroke();
  drawSpark(ctx, r * 0.9, -r * 1.5, 3 + spark * 3);
  // Body
  ctx.beginPath();
  ctx.arc(0, 0, r, 0, Math.PI * 2);
  ink(ctx, '#23262b', Math.min(INK_W, r * 0.2));
  ctx.fillStyle = 'rgba(255,255,255,0.22)';
  ctx.beginPath();
  ctx.ellipse(-r * 0.42, -r * 0.45, r * 0.26, r * 0.16, -0.7, 0, Math.PI * 2);
  ctx.fill();
  // Trefoil: a yellow disc with three black blades
  ctx.fillStyle = '#ffd23f';
  ctx.beginPath();
  ctx.arc(0, r * 0.1, r * 0.44, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = '#23262b';
  for (let i = 0; i < 3; i++) {
    const a = -Math.PI / 2 + (i * Math.PI * 2) / 3;
    ctx.beginPath();
    ctx.moveTo(0, r * 0.1);
    ctx.arc(0, r * 0.1, r * 0.44, a - 0.5, a + 0.5);
    ctx.closePath();
    ctx.fill();
  }
  ctx.fillStyle = '#ffd23f';
  ctx.beginPath();
  ctx.arc(0, r * 0.1, r * 0.13, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
}

/** A crossbow bolt pointing along +x after the caller's rotate: a dark shaft, red feather fletching and a bright steel tip. */
export function drawBolt(ctx: CanvasRenderingContext2D): void {
  for (const s of [-1, 1]) {
    ctx.beginPath();
    ctx.moveTo(-17, 0);
    ctx.lineTo(-25, s * 6);
    ctx.lineTo(-10, s * 1.5);
    ctx.closePath();
    ink(ctx, '#e8453c', 1.8);
  }
  inkLine(ctx, -18, 0, 10, 0, 3, '#5b4330', 1.4);
  ctx.beginPath();
  ctx.moveTo(19, 0);
  ctx.lineTo(8, -4.5);
  ctx.lineTo(8, 4.5);
  ctx.closePath();
  ink(ctx, '#e6ecf1', 1.8);
}

/** A TNT bundle centred on (x, y): three red sticks taped together with a fuse out of the top. `spark` 0..1 lights the fuse. */
export function drawTnt(ctx: CanvasRenderingContext2D, x: number, y: number, s = 1, spark = 0): void {
  ctx.save();
  ctx.translate(x, y);
  ctx.scale(s, s);
  for (const sx of [-7, 7, 0]) {
    rr(ctx, sx - 4.5, -12, 9, 24, 2.5);
    ink(ctx, '#d6362c', 2);
  }
  for (const ty of [-6, 4]) {
    rr(ctx, -12.5, ty, 25, 4.5, 1.5);
    ink(ctx, '#e8d9a0', 1.4);
  }
  ctx.fillStyle = 'rgba(255,255,255,0.3)';
  for (const sx of [-7, 0, 7]) ctx.fillRect(sx - 3, -10, 1.8, 20);
  ctx.beginPath();
  ctx.moveTo(0, -12);
  ctx.quadraticCurveTo(2, -20, 8, -19);
  ctx.strokeStyle = INK;
  ctx.lineWidth = 3.5;
  ctx.lineCap = 'round';
  ctx.stroke();
  ctx.strokeStyle = '#d8c48a';
  ctx.lineWidth = 1.6;
  ctx.stroke();
  if (spark > 0) drawSpark(ctx, 8, -19, 2.5 + spark * 2.5);
  ctx.restore();
}

/** A boulder: a grey rock with a lit top-left facet and a shaded lower-right one. */
export function drawBoulder(ctx: CanvasRenderingContext2D, x: number, y: number, r: number, rot: number): void {
  ctx.save();
  ctx.translate(x, y);
  ctx.rotate(rot);
  const bumps = [1, 0.9, 1.02, 0.86, 0.96, 1.04, 0.88, 0.95];
  ctx.beginPath();
  bumps.forEach((k, i) => {
    const a = (i / bumps.length) * Math.PI * 2;
    const px = Math.cos(a) * r * k;
    const py = Math.sin(a) * r * k;
    if (i === 0) ctx.moveTo(px, py);
    else ctx.lineTo(px, py);
  });
  ctx.closePath();
  ink(ctx, '#8b949e', Math.min(INK_W, r * 0.3));
  ctx.fillStyle = 'rgba(0,0,0,0.16)';
  ctx.beginPath();
  ctx.moveTo(r * 0.1, r * 0.1);
  ctx.lineTo(r * 0.9, r * 0.2);
  ctx.lineTo(r * 0.5, r * 0.8);
  ctx.closePath();
  ctx.fill();
  ctx.fillStyle = 'rgba(255,255,255,0.3)';
  ctx.beginPath();
  ctx.moveTo(-r * 0.6, -r * 0.3);
  ctx.lineTo(-r * 0.2, -r * 0.75);
  ctx.lineTo(0, -r * 0.3);
  ctx.closePath();
  ctx.fill();
  ctx.restore();
}

/** A small crossbow held at the origin and pointing along +x: a stock, steel limbs, a string and a bolt on the rail. */
export function drawCrossbow(ctx: CanvasRenderingContext2D): void {
  rr(ctx, -12, -2.5, 30, 5, 2);
  ink(ctx, '#8a5a2a', 2);
  rr(ctx, -10, 1, 8, 7, 2);
  ink(ctx, '#8a5a2a', 2);
  ctx.beginPath();
  ctx.moveTo(10, -14);
  ctx.quadraticCurveTo(17, 0, 10, 14);
  ctx.strokeStyle = INK;
  ctx.lineWidth = 5;
  ctx.lineCap = 'round';
  ctx.stroke();
  ctx.strokeStyle = '#5d6670';
  ctx.lineWidth = 2.5;
  ctx.stroke();
  ctx.strokeStyle = '#e6ecf1';
  ctx.lineWidth = 1.2;
  ctx.beginPath();
  ctx.moveTo(10, -14);
  ctx.lineTo(-4, -1);
  ctx.lineTo(10, 14);
  ctx.stroke();
  inkLine(ctx, -5, -1, 17, -1, 2, '#5b4330', 1);
  ctx.beginPath();
  ctx.moveTo(20, -1);
  ctx.lineTo(15, -3.5);
  ctx.lineTo(15, 1.5);
  ctx.closePath();
  ink(ctx, '#e6ecf1', 1.2);
}

/** A radio handset at (x, y): the airstrike caller's walkie-talkie with an antenna and a lit screen. */
export function drawHandset(ctx: CanvasRenderingContext2D, x: number, y: number): void {
  ctx.save();
  ctx.translate(x, y);
  inkLine(ctx, 3, -9, 6, -22, 2, '#8a949e', 1);
  rr(ctx, -5.5, -10, 11, 18, 2.5);
  ink(ctx, '#3a3f47', 2);
  ctx.fillStyle = '#7fe3ff';
  ctx.fillRect(-3.5, -7.5, 7, 4.5);
  ctx.fillStyle = '#ff5d5d';
  ctx.beginPath();
  ctx.arc(0, 2.5, 1.6, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = 'rgba(255,255,255,0.35)';
  ctx.fillRect(-4.5, -9, 1.6, 15);
  ctx.restore();
}

/** A teleporter at the origin: a small grey device with two prongs and a dial that glows cyan. `glow` 0..1. */
export function drawTeleporter(ctx: CanvasRenderingContext2D, glow: number): void {
  inkLine(ctx, -4.5, -6, -6, -12, 2.2, '#aab3bb', 1);
  inkLine(ctx, 4.5, -6, 6, -12, 2.2, '#aab3bb', 1);
  rr(ctx, -7.5, -6, 15, 13, 3);
  ink(ctx, '#5d6670', 2);
  ctx.fillStyle = `rgba(120, 230, 255, ${0.2 + 0.3 * glow})`;
  ctx.beginPath();
  ctx.arc(0, 0.5, 6, 0, Math.PI * 2);
  ctx.fill();
  ctx.beginPath();
  ctx.arc(0, 0.5, 3.4, 0, Math.PI * 2);
  ink(ctx, `rgba(140, 236, 255, ${0.6 + 0.4 * glow})`, 1.5);
}

/**
 * A cartoon bomber pointing along +x at the origin: a stubby pale body, a wing slab, a red tail and a
 * propeller disc. `spin` turns the prop; the caller scales it to taste.
 */
export function drawPlane(ctx: CanvasRenderingContext2D, spin: number): void {
  // Tail fin and the far half of the wing sit behind the body
  ctx.beginPath();
  ctx.moveTo(-22, -5);
  ctx.lineTo(-30, -20);
  ctx.lineTo(-19, -20);
  ctx.lineTo(-12, -5);
  ctx.closePath();
  ink(ctx, '#e8453c', 2.5);
  rr(ctx, -12, -10, 30, 7, 3.5);
  ink(ctx, '#8a949e', 2.5);
  // Body
  rr(ctx, -26, -7, 52, 15, 7.5);
  ink(ctx, '#d9dde3', 2.5);
  ctx.fillStyle = 'rgba(255,255,255,0.45)';
  rr(ctx, -20, -5, 40, 3, 1.5);
  ctx.fill();
  // Cockpit
  rr(ctx, 2, -12, 13, 8, 3);
  ink(ctx, '#8fdcff', 2);
  // Near wing over the body, and the bomb bay seam
  rr(ctx, -8, -1, 26, 7, 3.5);
  ink(ctx, '#aab3bb', 2.5);
  ctx.fillStyle = INK;
  ctx.fillRect(-14, 6, 20, 1.5);
  // Propeller: a hub and two blades
  ctx.save();
  ctx.translate(28, 0.5);
  ctx.rotate(spin);
  ctx.fillStyle = 'rgba(42,26,18,0.55)';
  rr(ctx, -2, -13, 4, 26, 2);
  ctx.fill();
  ctx.restore();
  ctx.beginPath();
  ctx.arc(28, 0.5, 3, 0, Math.PI * 2);
  ink(ctx, '#f0b429', 1.5);
}

/**
 * A jetski at the origin (the waterline) pointing along +x: a wedge hull in the team colour, a black
 * seat and a handlebar stem. The spray behind it is drawn by the caller, who knows the time.
 */
export function drawJetski(ctx: CanvasRenderingContext2D, color: string): void {
  // Handlebar stem first so the hull overlaps its foot
  inkLine(ctx, 16, -8, 24, -27, 4, '#8a949e', 1.2);
  inkLine(ctx, 16, -27, 32, -29, 3.2, '#2b2b2b', 1.2);
  rr(ctx, -24, -19, 34, 11, 5.5);
  ink(ctx, '#2b2b2b', 2);
  ctx.beginPath();
  ctx.moveTo(-36, -4);
  ctx.lineTo(-31, 9);
  ctx.lineTo(26, 9);
  ctx.lineTo(40, -1);
  ctx.lineTo(31, -10);
  ctx.lineTo(-26, -10);
  ctx.closePath();
  ink(ctx, color);
  ctx.fillStyle = 'rgba(255,255,255,0.4)';
  rr(ctx, -27, -8, 52, 4, 2);
  ctx.fill();
  ctx.fillStyle = 'rgba(0,0,0,0.18)';
  rr(ctx, -30, 2, 62, 5, 2);
  ctx.fill();
}

/**
 * A catapult at the origin (its ground line), facing +x after the caller's scale: two wheels, a
 * timber base, an A-frame with a padded stop, and the throwing arm with a cup at one end and a
 * counterweight at the other. `swing` is 0 with the arm cocked back and 1 with it against the stop;
 * `loaded` puts a boulder in the cup.
 */
export function drawCatapult(ctx: CanvasRenderingContext2D, swing: number, loaded: boolean): void {
  const WOOD = '#b0813f';
  const DARK = '#8a5a2a';
  const px = -12;
  const py = -40;
  const L = 38;
  const angle = -2.79 + (-1.08 + 2.79) * swing;
  // Far wheel, base beam and the back strut sit behind the arm
  wheel(ctx, 24, -12, 12, DARK);
  rr(ctx, -42, -27, 84, 10, 3);
  ink(ctx, WOOD);
  inkLine(ctx, -30, -24, px, py, 7, WOOD, 1.6);
  // Arm: counterweight end short, cup end long
  const cx = Math.cos(angle);
  const cy = Math.sin(angle);
  inkLine(ctx, px - cx * 16, py - cy * 16, px + cx * L, py + cy * L, 7, WOOD, 1.6);
  ctx.save();
  ctx.translate(px, py);
  ctx.rotate(angle);
  rr(ctx, -24, -7, 14, 14, 3);
  ink(ctx, '#4b525b', 2);
  ctx.fillStyle = 'rgba(255,255,255,0.25)';
  ctx.fillRect(-22, -5, 3, 9);
  // Cup: a bowl whose opening faces the arm's +y side, which is up when cocked and forward at the stop
  ctx.beginPath();
  ctx.arc(L, 0, 9, Math.PI, 0);
  ctx.closePath();
  ink(ctx, DARK, 2.2);
  if (loaded) drawBoulder(ctx, L, -4, 10, 0.3);
  ctx.restore();
  // Front strut with the padded stop, and the pivot pin over the arm
  inkLine(ctx, 18, -24, 2, -60, 7, WOOD, 1.6);
  rr(ctx, -6, -66, 18, 8, 3);
  ink(ctx, '#6b4423', 2);
  ctx.beginPath();
  ctx.arc(px, py, 4, 0, Math.PI * 2);
  ink(ctx, '#4b525b', 2);
  wheel(ctx, -24, -12, 12, DARK);
}

function wheel(ctx: CanvasRenderingContext2D, x: number, y: number, r: number, color: string): void {
  ctx.beginPath();
  ctx.arc(x, y, r, 0, Math.PI * 2);
  ink(ctx, color, 2.5);
  ctx.strokeStyle = INK;
  ctx.lineWidth = 2;
  ctx.beginPath();
  for (let i = 0; i < 4; i++) {
    const a = (i / 4) * Math.PI;
    ctx.moveTo(x + Math.cos(a) * r, y + Math.sin(a) * r);
    ctx.lineTo(x - Math.cos(a) * r, y - Math.sin(a) * r);
  }
  ctx.stroke();
  ctx.beginPath();
  ctx.arc(x, y, r * 0.3, 0, Math.PI * 2);
  ink(ctx, '#4b525b', 1.5);
}

/** A tongue of fire standing on (x, y): an orange lick with a yellow core, `h` tall, its tip leaning `lean` px. */
export function drawFlame(ctx: CanvasRenderingContext2D, x: number, y: number, h: number, lean: number): void {
  const w = h * 0.5;
  ctx.fillStyle = '#ff6a1a';
  ctx.beginPath();
  ctx.moveTo(x - w, y);
  ctx.quadraticCurveTo(x - w * 0.9, y - h * 0.55, x + lean, y - h);
  ctx.quadraticCurveTo(x + w * 0.9, y - h * 0.55, x + w, y);
  ctx.closePath();
  ctx.fill();
  ctx.fillStyle = '#ffd23f';
  ctx.beginPath();
  ctx.moveTo(x - w * 0.45, y);
  ctx.quadraticCurveTo(x - w * 0.4, y - h * 0.35, x + lean * 0.5, y - h * 0.55);
  ctx.quadraticCurveTo(x + w * 0.4, y - h * 0.35, x + w * 0.45, y);
  ctx.closePath();
  ctx.fill();
}

/**
 * The tunneler: a squat tracked boring machine drawn facing +x, centred on its body, so the
 * caller rotates it along its velocity. `spin` 0..1 turns the drill bit (the flutes crawl along
 * the cone) and `step` 0..1 crawls the track treads so it reads as driving, not sliding.
 */
export function drawTunneler(ctx: CanvasRenderingContext2D, spin: number, step: number): void {
  // Tracks: a rounded rail with tread marks that crawl backwards.
  rr(ctx, -20, 2, 36, 11, 5.5);
  ink(ctx, '#3a3f47', 2.5);
  ctx.strokeStyle = '#6b7079';
  ctx.lineWidth = 2;
  ctx.lineCap = 'round';
  const shift = (step % 1) * 7;
  for (let i = 0; i < 5; i++) {
    const tx = -16 + i * 7 - shift + 7;
    if (tx < -16 || tx > 12) continue;
    ctx.beginPath();
    ctx.moveTo(tx, 4);
    ctx.lineTo(tx - 1.5, 11);
    ctx.stroke();
  }
  // Body and cab.
  rr(ctx, -18, -10, 30, 14, 4);
  ink(ctx, '#e0a32a', 2.5);
  rr(ctx, -14, -19, 14, 11, 3);
  ink(ctx, '#f1c75a', 2.5);
  rr(ctx, -10, -16, 7, 6, 1.5);
  ink(ctx, '#bfe9ff', 1.5);
  // Exhaust stack on the back of the cab.
  rr(ctx, -17, -24, 4, 8, 1.5);
  ink(ctx, '#4b525b', 1.5);
  // Hazard stripe across the body.
  ctx.fillStyle = INK;
  for (let i = 0; i < 3; i++) ctx.fillRect(-9 + i * 8, -8, 3, 10);
  // The bit: a cone with ridges that run along it as it turns, on a short collar.
  rr(ctx, 10, -8, 6, 10, 1.5);
  ink(ctx, '#8a949e', 2);
  ctx.beginPath();
  ctx.moveTo(16, -9);
  ctx.lineTo(38, -3);
  ctx.lineTo(38, 3);
  ctx.lineTo(16, 7);
  ctx.closePath();
  ink(ctx, '#c9d1d9', 2.5);
  ctx.strokeStyle = '#5c646d';
  ctx.lineWidth = 1.8;
  for (let i = 0; i < 4; i++) {
    const f = ((i / 4 + spin) % 1);
    const x = 17 + f * 20;
    const hw = 8 - f * 5;
    ctx.beginPath();
    ctx.moveTo(x, -hw);
    ctx.lineTo(x + 3, hw);
    ctx.stroke();
  }
}

/** The tunneler as it is carried: a crate with the drill's nose sticking out of one side, sized for a paw. */
export function drawTunnelerCrate(ctx: CanvasRenderingContext2D, x: number, y: number): void {
  ctx.save();
  ctx.translate(x, y);
  rr(ctx, -9, -8, 18, 14, 2.5);
  ink(ctx, '#c98c4e', 2);
  ctx.strokeStyle = shade('#c98c4e', -50);
  ctx.lineWidth = 1.5;
  ctx.beginPath();
  ctx.moveTo(-9, -3);
  ctx.lineTo(9, -3);
  ctx.moveTo(-9, 2);
  ctx.lineTo(9, 2);
  ctx.stroke();
  ctx.beginPath();
  ctx.moveTo(9, -4);
  ctx.lineTo(16, -1);
  ctx.lineTo(9, 2);
  ctx.closePath();
  ink(ctx, '#c9d1d9', 1.8);
  ctx.restore();
}
